import { createSlice, PayloadAction, createAction } from "@reduxjs/toolkit";
import { CategoriesType } from "../../types/categoriesType";
import { RootState } from "../store";
import { ErrorType } from "../../types/errorType";
import { API_ENDPOINTS } from "Config/apiConfig";
import {
  AppTypeWithData,
  initialState,
  createFetchInitialDataThunk,
  createFetchProgressiveDataThunk,
  ApiResponse,
} from "../../utils/fetchDataUtils";

// Initial state
const initialCategoriesState: AppTypeWithData<CategoriesType> = initialState<CategoriesType>();

// Action creators
export const appendDataAction = createAction<CategoriesType[]>("categoriesList/appendData");
export const setProgressAction = createAction<number>("categoriesList/setProgress");

// Reducer functions
const appendData = (state: AppTypeWithData<CategoriesType>, action: PayloadAction<CategoriesType[]>) => {
  const uniqueRecords = action.payload.filter(
    (newRecord) => !state.data.some((existingRecord) => existingRecord.id === newRecord.id)
  );
  state.data.push(...uniqueRecords); // Append only unique records
};

const setProgress = (state: AppTypeWithData<CategoriesType>, action: PayloadAction<number>) => {
  state.progress = action.payload;
};

// Thunks for fetching data
export const fetchInitialData = createFetchInitialDataThunk<CategoriesType>("categories", API_ENDPOINTS.categories);

export const fetchProgressiveData = createFetchProgressiveDataThunk<CategoriesType>(
  "categories",
  API_ENDPOINTS.categories,
  (state: RootState) => state.categoriesList.totalRecords,
  (state: RootState) => state.categoriesList.data.length,
  appendDataAction,
  setProgressAction
);

// Slice definition
const categoriesListSlice = createSlice({
  name: "categoriesList",
  initialState: initialCategoriesState,
  reducers: {
    appendData,
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setProgress,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchInitialData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchInitialData.fulfilled, (state, action: PayloadAction<ApiResponse<CategoriesType>>) => {
      state.data = action.payload.results;
      state.totalRecords = action.payload.totalRecords;
      state.totalPages = action.payload.totalPages;
      state.page = action.payload.page;
      state.loading = false;
      state.isInitialLoadComplete = true;
    });
    builder.addCase(fetchInitialData.rejected, (state, action) => {
      state.error = action.payload as ErrorType;
      state.loading = false;
    });
    builder.addCase(fetchProgressiveData.pending, (state) => {
      state.progressiveLoading = true;
      state.progress = 0;
      state.error = null;
    });
    builder.addCase(fetchProgressiveData.fulfilled, (state) => {
      state.progressiveLoading = false;
      state.progress = 100;
    });
    builder.addCase(fetchProgressiveData.rejected, (state, action) => {
      state.error = action.payload as ErrorType;
      state.progressiveLoading = false;
    });
  },
});

// Export actions and reducer
export const { setPageSize, setCurrentPage } = categoriesListSlice.actions;
export const categoriesRootState = (state: RootState) => state.categoriesList;
export default categoriesListSlice.reducer;
